import React, { useEffect, useState, useCallback, useMemo } from "react";
import debounce from 'lodash/debounce';
import { useNavigate, useLocation } from "react-router-dom";
import { parseISO, format } from "date-fns";
import QuotationService from "services/QuotationService";
import AdditionalExpTypeService from "services/AdditionalExpTypeService";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import MDAccordionSimple from "components/MDAccordion/MDAccordionSimple";
import MDAccordionTable from "components/MDAccordion/MDAccordionTable";
import LoadingComponent from "assets/theme/functions/loadingAnimation";
import QuotationHeader from "layouts/quotations/QuotationHeader";
import PaymentConditionService from "services/PaymentConditionService";
import PaymentFormService from "services/PaymentFormService";
import AttachmentService from "services/AttachmentService";
import TaxTypeService from "services/TaxTypeService";
import TennantConfigService from "services/TennantConfigService";
import { Modal, TextField, Snackbar, Alert, Dialog, Button, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NumericFormat } from "react-number-format";
import MDTypography from "components/MDTypography";
import ShippingTypeService from "services/ShippingTypeService";
import { QUOTATION_STATUS_LIST } from "statusConstants";
import { useAuth } from "context/authContext";
import { normalizeLinePricing, normalizeLinePricingCollection } from "utils/linePricing";
import { upperCase } from "lodash";

const isUnauthorizedError = (error) => error?.response?.status === 401;

const QuotationDetails = (id, password) => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openCancelQuotationDialog, setOpenCancelQuotationDialog] = useState(false);
  const [openDenyQuotationDialog, setOpenDenyQuotationDialog] = useState(false);
  const [quotation, setQuotation] = useState(null);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [paymentConditions, setPaymentConditions] = useState([{}]);
  const [paymentForms, setPaymentForms] = useState([{}]);
  const [taxTypes, setTaxTypes] = useState([]);
  const [selectedTaxItems, setSelectedTaxItems] = useState([{}]);
  const [selectedItemTax, setSelectedItemTax] = useState({});
  const [paymentFormsList, setPaymentFormsList] = useState([{}]);
  const [paymentConditionsList, setPaymentConditionsList] = useState([{}]);
  const [tennantConfigs, setTennantConfigs] = useState({});
  const [shippingTypesList, setShippingTypesList] = useState([{}]);
  const [editedTaxItems, setEditedTaxItems] = useState([{}]);
  const [footerValues, setFooterValues] = useState({ TotalBefDiscount: '', FinanceDiscount: '', OtherCostsSum: 0, Rounding: 0, TaxSum: 0, DocTotal: 0, });
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState({}); // Para erros por linha
  const [ipiExpenseType, setIpiExpenseType] = useState([]);
  const [refresh, setRefrash] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const { token, user, isAuthenticated } = useAuth();
  const [loadingMessage, setLoadingMessage] = useState({ error: false, message: "" });
  const { state } = useLocation();
  const [showLogo, setShowLogo] = useState(true);

  let templocalTaxItems = [...selectedTaxItems];
  let tempQuotation = { ...quotation };
  const editableQuotationStatuses = ['O', 'P', 'Criada', 'Preenchida'];

  const calculateQuotationLineTotals = useCallback((rawLine, availableTaxTypes = []) => {
    const line = normalizeLinePricing(rawLine);
    const lineTotal = Number(line.LineTotal) || 0;
    const ipiTaxTypeId = availableTaxTypes.find((taxType) => taxType.Description === "IPI")?.Id;

    const recalculatedQuotationLineTax = (line.QuotationLineTax || []).map((currentTax) => {
      const taxBasePercent = parseFloat(currentTax.TaxBasePercent) || 100;
      const taxRate = parseFloat(currentTax.TaxRate) || 0;
      const taxInPrice = typeof currentTax.TaxInPrice === "boolean"
        ? currentTax.TaxInPrice
        : !!availableTaxTypes.find((taxType) => String(taxType.Id) === String(currentTax.TaxTypeId))?.TaxInPrice;

      let taxSum = (lineTotal * (taxBasePercent / 100) * taxRate) / 100;
      taxSum = isNaN(taxSum) ? 0 : parseFloat(taxSum.toFixed(2));

      return {
        ...currentTax,
        TaxInPrice: taxInPrice,
        TaxSum: taxSum,
      };
    });

    const taxOutSum = recalculatedQuotationLineTax.reduce((tax, currentTax) => {
      return tax + (currentTax.TaxInPrice ? 0 : (Number(currentTax.TaxSum) || 0));
    }, 0);

    const ipiTotal = recalculatedQuotationLineTax.reduce((tax, currentTax) => {
      return tax + (String(currentTax.TaxTypeId) === String(ipiTaxTypeId) ? (Number(currentTax.TaxSum) || 0) : 0);
    }, 0);

    return {
      ...line,
      QuotationLineTax: recalculatedQuotationLineTax,
      LineTaxOutPriceTotal: taxOutSum,
      IPITotal: ipiTotal,
      LineWithTaxTotal: lineTotal + taxOutSum,
    };
  }, []);

  const calculateQuotationFooterValues = useCallback((quotationLines = [], additionalExpenses = [], financeDiscount = 0) => {
    const totalBefDiscount = quotationLines.reduce((total, line) => total + (Number(line.LineTotal) || 0), 0);
    const taxSum = quotationLines.reduce((tax, line) => tax + (Number(line.LineTaxOutPriceTotal) || 0), 0);
    const ipiTotal = quotationLines.reduce((tax, line) => tax + (Number(line.IPITotal) || 0), 0);
    const ipiExpenseTypeId = (Array.isArray(ipiExpenseType) ? ipiExpenseType : []).find((type) => type.Description === 'IPI')?.Id;

    const filteredExpenses = ipiExpenseTypeId != null
      ? additionalExpenses.filter((expense) => Number(expense.ExpenseTypeId) !== Number(ipiExpenseTypeId))
      : additionalExpenses;

    const otherCostsSum = filteredExpenses.reduce((cost, expense) => cost + (Number(expense.Value) || 0), 0);
    const discountPercent = Number(financeDiscount) || 0;
    const docTotal = (totalBefDiscount + otherCostsSum + taxSum) * (1 - (discountPercent / 100));

    return {
      TotalBefDiscount: totalBefDiscount,
      TaxSum: taxSum,
      OtherCostsSum: otherCostsSum,
      IPITotal: ipiTotal,
      FinanceDiscount: discountPercent,
      DocTotal: docTotal,
    };
  }, [ipiExpenseType]);

  useEffect(() => {
    try {
      const handleScroll = () => {
        setShowLogo(window.scrollY === 0);
      };

      window.addEventListener("scroll", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
    catch (error) {

    }
  })

  useEffect(() => {

    const init = async () => {
      const authenticated = await isAuthenticated();
      if (!password && !authenticated) {
        navigate(`/access-quotation/${id}`);
        return;
      }

      if (Number(state?.id) === 1) {
        setSnackbar({
          open: true,
          message: state.error,
          severity: "error",
        })
      }

      fetchData();
    };

    const fetchData = async () => {
      try {
        const quotationResponse = await QuotationService.GetQuotations(
          id,
          password
        );

        const quotationData = quotationResponse.data.data[0];
        if (!quotationData) {
          setLoadingMessage({ error: true, message: "Não foi possível carregar a página: nenhuma cotação foi encontrada." });
        }

        if (!password && quotationData?.UserEmail !== user?.Email) {
          navigate(`/access-quotation/${id}`);
          return;
        }
        
        // Em seguida, busca os dados relacionado
        const [
          expenseTypesResponse,
          paymentConditionsResponse,
          paymentFormResponse,
          taxTypeResponse,
          shippingTypeResponse,
          tennantConfigsResponse,
          attachmentResponse
        ] = await Promise.all([
          AdditionalExpTypeService.GetAdditionalExpenseTypes(quotationData.TennantId),
          PaymentConditionService.GetPaymentConditions(quotationData.TennantId),
          PaymentFormService.GetPaymentForms(quotationData.TennantId),
          TaxTypeService.GetTaxTypes(quotationData.TennantId),
          ShippingTypeService.GetShippingTypes(quotationData.TennantId),
          TennantConfigService.GetTennantConfigs(quotationData.TennantId),
          AttachmentService.getQuotationAttachments(quotationData.Guid, quotationData.TennantId)
        ]);

        if (expenseTypesResponse?.data?.data?.length === 0) {
          setLoadingMessage({ error: true, message: "Não foi possível carregar a página: tipos de despesa não foram encontrados." });
          return;
        }

        if (paymentConditionsResponse?.data?.data?.length === 0) {
          setLoadingMessage({ error: true, message: "Não foi possível carregar a página: condições de pagamento não foram encontradas." });
          return;
        }

        if (paymentFormResponse?.data?.data?.length === 0) {
          setLoadingMessage({ error: true, message: "Não foi possível carregar a página: formas de pagamento não foram encontradas." });
          return;
        }

        if (taxTypeResponse?.data?.data?.length === 0) {
          setLoadingMessage({ error: true, message: "Não foi possível carregar a página: tipos de imposto não foram encontrados." });
          return;
        }

        if (shippingTypeResponse?.data?.data?.length === 0) {
          setLoadingMessage({ error: true, message: "Não foi possível carregar a página: tipos de frete não foram encontrados." });
          return;
        }

        const formattedQuotation = formatQuotationData(
          quotationData,
          expenseTypesResponse.data.data,
          paymentConditionsResponse.data.data,
          paymentFormResponse.data.data,
          shippingTypeResponse.data.data,
          taxTypeResponse.data.data
        );

        setQuotation(formattedQuotation);
        setAttachments(attachmentResponse.data.data);
        setTennantConfigs(tennantConfigsResponse.data.data);
        setPaymentConditions(paymentConditionsResponse.data.data);
        setPaymentForms(paymentFormResponse.data.data);
        setExpenseTypes(
          expenseTypesResponse.data.data
            .filter(expenseType => expenseType.Description !== 'IPI')
            .sort((a, b) => a.Id - b.Id)
        );
        setIpiExpenseType(expenseTypesResponse.data.data
          .filter(expenseType => expenseType.Description === 'IPI'));
        setTaxTypes(taxTypeResponse.data.data);

        setShippingTypesList(
          shippingTypeResponse.data.data.map(({ Id, ExtDescription }) => ({
            Id,
            Description: ExtDescription,
          }))
        );

        setPaymentConditionsList(
          paymentConditionsResponse.data.data.map(({ Id, ExtDescription }) => ({
            Id,
            Description: ExtDescription,
          }))
        );

        setPaymentFormsList(
          paymentFormResponse.data.data.map(({ Id, ExtDescription }) => ({
            Id,
            Description: ExtDescription,
          }))
        );



        // Buscar o tipo de despesa IPI do array de expense types
        const ipiExpenseTypeFromArray = expenseTypesResponse.data.data.find(type => type.Description === 'IPI');
        const ipiExpenseTypeId = ipiExpenseTypeFromArray?.Id;

        // Filtre as despesas para excluir aquelas com ExpenseTypeId igual ao ipiExpenseTypeId
        const filteredExpenses = ipiExpenseTypeId
          ? quotationData.AdditionalExpenses.filter(expense => expense.ExpenseTypeId !== ipiExpenseTypeId)
          : quotationData.AdditionalExpenses;

        // Calcule OtherCostsSum com as despesas filtradas
        const costs = filteredExpenses.reduce((cost, line) => cost + (Number(line.Value) || 0), 0);


        const discount = quotationData.FinanceDiscount;
        setFooterValues((footerValues) => ({
          ...footerValues,
          ...calculateQuotationFooterValues(
            formattedQuotation.QuotationLines,
            quotationData.AdditionalExpenses,
            discount,
          ),
        }));
      } catch (error) {
        if (!isUnauthorizedError(error)) {
          console.error("Erro ao buscar dados:", error);
        }
      }
    };

    init();

  }, [refresh, navigate, user]);

  const attachmentItems = () => {
    if (loadingAttachments) {
      return [{
        OriginalName: "Carregando...",
        Size: "...",
        Description: "...",
        Baixar: "Carregando..."
      }];
    }

    if (!attachments || attachments.length === 0) {
      return [{
        OriginalName: "Nenhum anexo encontrado",
        Size: "-",
        Description: "-",
        Baixar: "-"
      }];
    }

    return attachments.map((attachment, index) => ({
      OriginalName: attachment.OriginalName || "Nome não disponível",
      Size: attachment.Size ? `${(attachment.Size / 1024).toFixed(2)} KB` : "Tamanho não disponível",
      Description: attachment.Description || "Sem descrição",
      Baixar: "Download",
      _originalData: attachment,
      _index: index
    }));
  };

  // Função para fazer download de anexos
  const handleDownloadAttachment = async (attachment) => {
    try {
      await AttachmentService.downloadAttachment(attachment.Url, attachment.OriginalName);
      setSnackbar({
        open: true,
        message: `Download de "${attachment.OriginalName}" iniciado com sucesso`,
        severity: "success"
      });
    } catch (error) {
      console.error("Erro ao baixar anexo:", error);
      setSnackbar({
        open: true,
        message: "Erro ao baixar anexo",
        severity: "error"
      });
    }
  };

  const handleAttachmentButtonClick = useCallback((index, fieldName, value, rowData) => {
    if (fieldName === 'Baixar') {
      // Obter os dados atuais dos attachmentItems
      const currentAttachmentItems = attachmentItems();

      // Verificar se é um item de placeholder (loading ou empty)
      const currentItem = currentAttachmentItems[index];
      if (!currentItem || !currentItem._originalData) {
        setSnackbar({
          open: true,
          message: "Nenhum anexo disponível para download",
          severity: "warning"
        });
        return;
      }

      // Usar os dados do _originalData
      const attachment = currentItem._originalData;

      if (attachment && attachment.OriginalName && attachment.Url) {
        handleDownloadAttachment(attachment);
      } else {
        console.error("❌ Dados do anexo incompletos:", attachment);
        setSnackbar({
          open: true,
          message: "Erro: dados do anexo incompletos",
          severity: "error"
        });
      }
    }
  }, [attachmentItems, handleDownloadAttachment]); // Mudança: usar attachmentItems como dependência

  const formatQuotationData = (data, expTypes, payConditions, payForms, shippingTypes, taxTypes) => {
    const formatDate = (dateString) => {
      let teste = dateString
        ? format(parseISO(dateString), "yyyy-MM-dd")
        : null;
      return teste;
    };

    return {
      Guid: data.Guid,
      StatusCode: data.Status,
      Status: QUOTATION_STATUS_LIST.find(status => status.Id === data.Status)?.Description || data.Status,
      RefNumber: data.RefNumber,
      CompanyName: data.Tennant.CompanyName,
      Base64Logo: data.Tennant?.Base64Logo ?? "",
      TennantId: data.Tennant?.Id,
      SupplierCompanyName: data?.Supplier?.CompanyName,
      BranchTaxId: data?.BranchTaxId,
      BranchName: data?.BranchName,
      ExternalId: data.ExternalId,
      SupplierTaxId: data?.Supplier?.TaxId,
      SupplierSalesperson: user?.Name,
      DeliveryAddressStreet: data.DeliveryAddressStreet,
      DeliveryAddressNum: data.DeliveryAddressNum,
      DeliveryAddressBlock: data.DeliveryAddressBlock,
      DeliveryAddressZip: data.DeliveryAddressZip,
      DeliveryAddressCity: data.DeliveryAddressCity,
      DeliveryAddressState: data.DeliveryAddressState,
      DeliveryAddressBuilding: data.DeliveryAddressBuilding,
      BillingAddressStreet: data.BillingAddressStreet,
      BillingAddressNum: data.BillingAddressNum,
      BillingAddressBlock: data.BillingAddressBlock,
      BillingAddressZip: data.BillingAddressZip,
      BillingAddressCity: data.BillingAddressCity,
      BillingAddressState: data.BillingAddressState,
      PaymentFormId: data.PaymentFormId ?? payForms[0]?.Id,
      PaymentConditionId: data.PaymentConditionId ?? payConditions[0]?.Id,
      ShippingTypeId: data.ShippingTypeId ?? shippingTypes[0]?.Id,
      DocTotal: data.DocTotal,
      Comments: data.Comments,
      SupplierComments: data.SupplierComments,
      Header: data.Header,
      Footer: data.Footer,
      FinanceDiscount: data.FinanceDiscount,
      DocDate: formatDate(data.DocDate),
      DeliveryExpectedDate: formatDate(data.DeliveryExpectedDate),
      QuotationLines: normalizeLinePricingCollection(data.QuotationLines).map((line) => {
        const formattedLine = {
          ...line,
          NecessaryDate: formatDate(line.NecessaryDate),
          ExpectedDeliveryDate: formatDate(line.ExpectedDeliveryDate),
          Usage: line.Usage,
          UomGroup: line.UomGroup,
          UnitOfMeasurement: line.UnitOfMeasurement,
          QuotationLineTax: (line.QuotationLineTax || []).map((tax) => ({
            Id: tax.Id,
            LineId: line.Id,
            TaxTypeId: tax.TaxTypeId,
            TaxRate: tax.TaxRate,
            TaxBasePercent: tax.TaxBasePercent,
            TaxBaseFree: tax.TaxBaseFree,
            TaxBaseOthers: tax.TaxBaseOthers,
            TaxInPrice: !!taxTypes.find((t) => t.Id === tax.TaxTypeId)?.TaxInPrice,
          })),
        };

        return calculateQuotationLineTotals(formattedLine, taxTypes);
      }),
      AdditionalExpenses: data.AdditionalExpenses
        .filter(expense => expense.ExpenseTypeId) // ✅ Only send valid expenses
        .map((expense) => ({
          ...expense
        })),
    };
  };

  const buildTaxModalData = (quotation, lineIndex) => {
    const modalTaxData = quotation.QuotationLines.flatMap((line) => {
      return taxTypes.map((tax) => {
        const existingTax = line.QuotationLineTax.find(
          (t) => String(t.TaxTypeId) === String(tax.Id)
        );
        return {
          Id: existingTax ? existingTax.Id : null,
          LineId: line.Id,
          TaxTypeId: tax.Id,
          Description: tax.Description,
          TaxBasePercent: existingTax ? existingTax.TaxBasePercent || 100 : 100,
          TaxRate: existingTax ? existingTax.TaxRate : 0,
          TaxBaseFree: existingTax ? existingTax.TaxBaseFree || 0 : 0,
          TaxBaseOthers: existingTax ? existingTax.TaxBaseOthers || 0 : 0,
          TaxInPrice: tax.TaxInPrice,
          TaxSum: existingTax
            ? (parseFloat(line.LineTotal) *
              (parseFloat(existingTax.TaxBasePercent) / 100) *
              parseFloat(existingTax.TaxRate)) /
            100
            : 0,
        };
      });
    });

    setSelectedItemTax({
      ItemCode: quotation.QuotationLines[lineIndex]?.ExtItemCode,
      ItemName: quotation.QuotationLines[lineIndex]?.ItemName,
    });

    setSelectedTaxItems(() => {
      let tst = modalTaxData.filter(
        (tax) =>
          String(tax.LineId) === String(quotation.QuotationLines[lineIndex]?.Id)
      )
      return tst;
    }
    );

    setEditedTaxItems(
      modalTaxData.filter(
        (tax) =>
          String(tax.LineId) === String(quotation.QuotationLines[lineIndex]?.Id)
      )
    );
    setModalOpen(true);

  };

  const handleCancelQuotationClose = () => {
    setOpenCancelQuotationDialog(false);
  }

  const handleDenyQuotationClose = () => {
    setOpenDenyQuotationDialog(false);
  }

  const handleCancelQuotationClick = () => {
    setOpenCancelQuotationDialog(true);
  }

  const handleDenyQuotationClick = () => {
    setOpenDenyQuotationDialog(true);
  }

  const handleConfirmCancelQuotation = () => {
    const message = {
      msg: 'Oferta cancelada!'
    };
    handleQuotationUpdate(message, 'C');
  }

  const handleConfirmDenyQuotation = () => {
    const message = {
      msg: 'Negada a autoridade sobre a oferta!'
    };
    handleQuotationUpdate(message, 'R');
  }

  const handleUpdateClick = () => {
    setOpenConfirmDialog(true);
  }

  const handleConfirmClose = () => {
    setOpenConfirmDialog(false);
  }

  const handleSaveDraft = () => {
    const message = {
      msg: 'Oferta salva como esboço!'
    };
    handleQuotationUpdate(message);
  }

  const handleConfirm = () => {
    const message = {
      msg: 'Oferta enviada com sucesso!'
    };
    handleQuotationUpdate(message, 'P');
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleQuotationUpdate = async ({ msg }, status) => {
    try {
      const calculatedFooterValues = calculateQuotationFooterValues(
        quotation.QuotationLines,
        quotation.AdditionalExpenses,
        footerValues.FinanceDiscount,
      );

      const updatedQuotation = { ...quotation, Status: status || quotation.StatusCode, DocTotal: calculatedFooterValues.DocTotal, IntegrationStatus: "P" }
      await QuotationService.UpdateQuotation(id, updatedQuotation, null, { accessCode: password });

      let newQuotation = await QuotationService.GetQuotations(id);
      setQuotation(newQuotation.data.data[0]);
      setSnackbar({
        open: true,
        message: msg,
        severity: 'success',
      });
      setRefrash(!refresh);
    } catch (error) {
      console.error("Erro ao atualizar cotação:", error);
    }
    finally {
      handleConfirmClose();
      handleCancelQuotationClose();
      handleDenyQuotationClose();
    }
  };

  const renderAdditionalExpenses = () => {
    let teste = expenseTypes.map((type, index) => {
      const existingExpense = quotation.AdditionalExpenses?.find(
        (expense) => String(expense.ExpenseTypeId) === String(type.Id)
      ) || { Value: 0 };
      return {
        Description: type.Description,
        Value: parseFloat(existingExpense?.Value) || 0,
      };
    });

    return teste;
  };

  const handleAdditionalExpenseChange = useCallback((index, field, value) => {
    setQuotation((prevQuot) => {
      const updatedExpenses = [...prevQuot.AdditionalExpenses];
      if (field === "Value") {
        const expenseTypeId = expenseTypes[index]?.Id;
        const expenseIndex = updatedExpenses.findIndex(expense => String(expense.ExpenseTypeId) === String(expenseTypeId));

        if (expenseIndex !== -1) {
          updatedExpenses[expenseIndex] = {
            ...updatedExpenses[expenseIndex],
            Value: value,
          };
        } else {
          updatedExpenses.push({
            Id: null,
            ExpenseTypeId: expenseTypeId,
            Value: value,
          });
        }
      }
      const filteredExpenses = updatedExpenses.filter(
        (expense) => parseFloat(expense.Value) > 0
      );

      setFooterValues((prevFooter) => ({
        ...prevFooter,
        ...calculateQuotationFooterValues(
          prevQuot.QuotationLines,
          filteredExpenses,
          prevFooter.FinanceDiscount,
        ),
      }));

      return {
        ...prevQuot,
        AdditionalExpenses: filteredExpenses,
      };
    });

  }, [expenseTypes]);

  const handleHeaderChange = useCallback(
    (field, value) => {
      setQuotation((prev) => ({ ...prev, [field]: value }));
    }, []
  );

  const handleObservationsChange = (field, value) => {
    setQuotation((prev) => ({ ...prev, [field]: value }));
  };

  const handleFooterChange = useCallback(
    (field, value) => {
      const discount = Number(value) || 0;

      setFooterValues((prevFooterValues) => {
        return {
          ...prevFooterValues,
          ...calculateQuotationFooterValues(
            quotation?.QuotationLines || [],
            quotation?.AdditionalExpenses || [],
            discount,
          ),
          [field]: discount,
        };
      });

      setQuotation((prev) => {
        return {
          ...prev,
          FinanceDiscount: discount,
        }
      })
    }, []
  );

  const renderLines = () => {
    return quotation.QuotationLines.map((rawLine) => {
      const line = calculateQuotationLineTotals(rawLine, taxTypes);

      const taxes = line.QuotationLineTax.reduce((acc, tax) => {
        const taxType = taxTypes.find((t) => t.Id === tax.TaxTypeId);
        if (taxType) {
          acc[taxType.Description] = tax.TaxRate; // Usa a descrição como chave e taxa como valor
        }

        return acc;
      }, {});

      return {
        ...line,
        ...taxes, // Adiciona as propriedades dinamicamente ao objeto line
      };
    });

  };

  const renderFooterValues = () => {
    const calculatedQuotationLines = quotation.QuotationLines.map((line) => calculateQuotationLineTotals(line, taxTypes));
    return {
      ...footerValues,
      ...calculateQuotationFooterValues(
        calculatedQuotationLines,
        quotation.AdditionalExpenses,
        footerValues.FinanceDiscount,
      ),
    }
  }

  const recalculateQuotationLineTotals = useCallback((rawLine) => {
    const line = normalizeLinePricing(rawLine);
    const lineTotal = Number(line.LineTotal) || 0;

    const recalculatedQuotationLineTax = (line.QuotationLineTax || []).map((currentTax) => {
      const taxBasePercent = parseFloat(currentTax.TaxBasePercent) || 100;
      const taxRate = parseFloat(currentTax.TaxRate) || 0;

      let taxSum = (lineTotal * (taxBasePercent / 100) * taxRate) / 100;
      taxSum = isNaN(taxSum) ? 0 : parseFloat(taxSum.toFixed(2));

      return {
        ...currentTax,
        TaxSum: taxSum,
      };
    });

    const taxOutSum = recalculatedQuotationLineTax.reduce((tax, currentTax) => {
      return tax + (currentTax.TaxInPrice ? 0 : (Number(currentTax.TaxSum) || 0));
    }, 0);

    const ipiTaxTypeId = taxTypes.find((taxType) => taxType.Description === "IPI")?.Id;
    const ipiTotal = recalculatedQuotationLineTax.reduce((tax, currentTax) => {
      return tax + (currentTax.TaxTypeId === ipiTaxTypeId ? (Number(currentTax.TaxSum) || 0) : 0);
    }, 0);

    return {
      ...line,
      QuotationLineTax: recalculatedQuotationLineTax,
      LineTaxOutPriceTotal: taxOutSum,
      IPITotal: ipiTotal,
      LineWithTaxTotal: lineTotal + taxOutSum,
    };
  }, [taxTypes]);

  const handleItemChange = useCallback(
    (index, field, value) => {
      setQuotation((prev) => {
        const updatedQuotationLines = [...prev.QuotationLines];
        const updatedLine = { ...updatedQuotationLines[index] };

        // Atualiza o campo com o valor recebido
        if (["Quantity", "UnitPrice", "DiscountPercent", "UnitDiscountValue"].includes(field)) {
          updatedLine[field] = value ? Number(value) : 0;
        } else {
          updatedLine[field] = value;
        }

        updatedQuotationLines[index] = normalizeLinePricing(updatedLine, {
          preferDiscountPercent: field === "DiscountPercent",
        });

        const recalculatedQuotationLines = updatedQuotationLines.map((lineItem) =>
          recalculateQuotationLineTotals(lineItem)
        );

        const ipiTotal = recalculatedQuotationLines.reduce(
          (tax, line) => tax + (Number(line.IPITotal) || 0),
          0
        );

        const ipiExpenseTypeId = (Array.isArray(ipiExpenseType) ? ipiExpenseType : []).find((type) => type.Description === 'IPI')?.Id;

        let updatedAdditionalExpenses = [...prev.AdditionalExpenses];
        if (ipiExpenseTypeId) {
          const existingIpiExpenseIndex = updatedAdditionalExpenses.findIndex(
            (expense) => String(expense.ExpenseTypeId) === String(ipiExpenseTypeId)
          );

          if (existingIpiExpenseIndex !== -1) {
            updatedAdditionalExpenses[existingIpiExpenseIndex] = {
              ...updatedAdditionalExpenses[existingIpiExpenseIndex],
              Value: ipiTotal,
            };
          } else if (ipiTotal > 0) {
            updatedAdditionalExpenses.push({
              Id: null,
              ExpenseTypeId: ipiExpenseTypeId,
              Value: ipiTotal,
            });
          }

          updatedAdditionalExpenses = updatedAdditionalExpenses.filter(
            (expense) => String(expense.ExpenseTypeId) !== String(ipiExpenseTypeId) || Number(expense.Value) > 0
          );
        }

        const lineTotal = recalculatedQuotationLines.reduce(
          (total, line) => total + (line.LineTotal || 0),
          0
        );

        const taxSum = recalculatedQuotationLines.reduce(
          (tax, line) => tax + (Number(line.LineTaxOutPriceTotal) || 0),
          0
        );

        setFooterValues((prevFooterValues) => ({
          ...prevFooterValues,
          ...calculateQuotationFooterValues(
            recalculatedQuotationLines,
            updatedAdditionalExpenses,
            prevFooterValues.FinanceDiscount,
          ),
        }));

        return {
          ...prev,
          QuotationLines: recalculatedQuotationLines,
          AdditionalExpenses: updatedAdditionalExpenses,
        };
      });
    },
    [ipiExpenseType, recalculateQuotationLineTotals]
  );

  const handleButtonClick = (index, fieldName, value) => {
    // Atualiza a cotação no estado
    setQuotation((prevQuotation) => {
      const updatedQuotationLines = prevQuotation.QuotationLines.map((line, i) => {
        if (i === index) {
          return { ...line, [fieldName]: value };
        }
        return line;
      });

      const updatedQuotation = { ...prevQuotation, QuotationLines: updatedQuotationLines };

      // Chama buildTaxModalData com a cotação atualizada
      buildTaxModalData(updatedQuotation, index);

      return updatedQuotation;
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalSave = () => {

    if (Object.keys(errorMessages).length > 0) {
      setSnackbar({
        open: true,
        message: "Por favor, corrija os erros antes de salvar.",
        severity: "error",
      });
      return;
    }

    setEditedTaxItems(templocalTaxItems);
    setSelectedTaxItems(templocalTaxItems);

    setQuotation((prev) => {
      const updatedQuotationLines = [...prev.QuotationLines];

      // Encontra o índice da linha que queremos atualizar
      const lineIndex = updatedQuotationLines.findIndex(
        (line) => line.Id === templocalTaxItems[0]?.LineId
      );

      if (lineIndex === -1) {
        return prev;
      }

      const sanitizedEditedTaxItems = templocalTaxItems.map(
        ({ Description, ...rest }) => rest
      );

      updatedQuotationLines[lineIndex] = recalculateQuotationLineTotals({
        ...updatedQuotationLines[lineIndex],
        QuotationLineTax: sanitizedEditedTaxItems,
      });

      const taxSum = updatedQuotationLines.reduce(
        (tax, line) => tax + (Number(line.LineTaxOutPriceTotal) || 0),
        0
      );

      const totalBefDiscount = updatedQuotationLines.reduce(
        (total, line) => total + (Number(line.LineTotal) || 0),
        0
      );

      const newIpiTotal = updatedQuotationLines.reduce(
        (tax, line) => tax + (Number(line.IPITotal) || 0),
        0
      );

      const ipiExpenseTypeId = (Array.isArray(ipiExpenseType) ? ipiExpenseType : []).find((type) => type.Description === "IPI")?.Id;

      // Verifica se já existe uma despesa de IPI
      const existingIpiExpenseIndex = prev.AdditionalExpenses.findIndex(
        (expense) => String(expense.ExpenseTypeId) === String(ipiExpenseTypeId)
      );

      let updatedExpenses = [...prev.AdditionalExpenses];

      if (ipiExpenseTypeId) {  // ✅ Only add if ipiExpenseTypeId exists
        if (existingIpiExpenseIndex !== -1) {
          updatedExpenses[existingIpiExpenseIndex] = {
            ...updatedExpenses[existingIpiExpenseIndex],
            Value: newIpiTotal,
          };
        } else {
          updatedExpenses.push({
            Id: null,
            ExpenseTypeId: ipiExpenseTypeId,
            Value: newIpiTotal,
          });
        }
      } else {
        console.warn("IPI ExpenseType not found - skipping IPI expense addition");
      }

      setFooterValues((prevFooterValues) => ({
        ...prevFooterValues,
        ...calculateQuotationFooterValues(
          updatedQuotationLines,
          updatedExpenses,
          prevFooterValues.FinanceDiscount,
        ),
      }));

      setSnackbar({
        open: true,
        message: "Impostos salvos com sucesso!",
        severity: "success",
      });

      return { ...prev, QuotationLines: updatedQuotationLines, AdditionalExpenses: updatedExpenses };
    });

    setModalOpen(false);
  };

  const handleTaxCellChange = (rowIndex, field, value) => {
    const updatedItems = [...templocalTaxItems];

    const taxItem = {
      ...updatedItems[rowIndex],
      [field]: parseFloat(value) || 0,
    };

    // Recupera LineTotal da linha associada
    const lineId = taxItem.LineId;
    const currentLine = quotation?.QuotationLines.find(
      (line) => String(line.Id) === String(lineId)
    );
    const lineTotal = currentLine ? parseFloat(currentLine.LineTotal) || 0 : 0;

    // Verifica se o campo alterado é TaxRate ou TaxBasePercent
    if (field === "TaxRate" || field === "TaxBasePercent") {
      const taxBasePercent = parseFloat(taxItem.TaxBasePercent) || 100;
      const taxRate = parseFloat(taxItem.TaxRate) || 0;

      // Recalcula TaxSum
      let taxSum = (lineTotal * (taxBasePercent / 100) * taxRate) / 100;

      // Verifica se o resultado é válido
      taxSum = isNaN(taxSum) ? 0 : parseFloat(taxSum.toFixed(2));

      // Atualiza TaxSum no objeto taxItem
      taxItem.TaxSum = taxSum;
    }

    // Atualiza o item na lista
    updatedItems[rowIndex] = taxItem;

    templocalTaxItems = updatedItems;
  };


  const taxModalFields = {
    columns: [
      { Header: "Imposto", accessor: "Description", align: "center" },
      {
        Header: "Alíquota",
        accessor: "TaxRate",
        align: "center",
        Cell: ({ row: { index }, value }) => (
          <NumericFormat
            value={value}
            variant="outlined"
            size="small"
            customInput={TextField}
            decimalSeparator=","
            thousandSeparator="."
            onValueChange={(values) => {
              const formattedValue = values.floatValue; // Armazena o valor numérico no estado
              handleTaxCellChange(index, "TaxRate", formattedValue);
            }}
            style={{ width: "100%", textAlign: "center" }}
            InputProps={{
              inputProps: { min: 0, max: 100, step: 0.01 },
              endAdornment: " %",
            }}
          />
        ),
      },
      {
        Header: "Base de Cálculo",
        accessor: "TaxBasePercent",
        align: "center",
        Cell: ({ row: { index }, value }) => (
          <NumericFormat
            value={value}
            variant="outlined"
            size="small"
            customInput={TextField}
            decimalSeparator=","
            thousandSeparator="."
            onValueChange={(values) => {
              const formattedValue = values.floatValue; // Armazena o valor numérico no estado
              handleTaxCellChange(index, "TaxBasePercent", formattedValue);
            }}
            style={{ width: "100%", textAlign: "center" }}
            error={Boolean(errorMessages[index])}
            InputProps={{
              inputProps: { min: 0, max: 100, step: 0.01 },
              endAdornment: " %",
            }}
          />
        ),
      },
      {
        Header: "Método Cálculo",
        accessor: "TaxInPrice",
        align: "center",
        Cell: ({ row: { index }, value }) => (
          <>
            {value
              ? "Imposto por dentro"
              : "Imposto por Fora"}
          </>
        ),
      },
      {
        Header: "Valor",
        accessor: "TaxSum",
        align: "center",
        type: "currency",
        Cell: ({ value }) => (
          <NumericFormat
            value={value}
            displayType={"text"}
            thousandSeparator={"."}
            decimalSeparator={","}
            prefix={"R$ "}
            decimalScale={2}
            fixedDecimalScale
          />
        ),
      },
    ],
  };


  const formFields = {
    Quotation: [
      //{ label: "ID", name: "Guid", width: 2, editable: false, type: "text" },
      {
        label: "ID Externo",
        name: "ExternalId",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Nº Proposta/Orçamento",
        name: "RefNumber",
        width: 1,
        editable: true,
        type: "text",
      },
      {
        label: "Status",
        name: "Status",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Data da Cotação",
        name: "DocDate",
        width: 1,
        editable: false,
        type: "date",
      },
      {
        label: "Data Necessária",
        name: "DeliveryExpectedDate",
        width: 1,
        editable: false,
        type: "date",
      },
    ],
    Buyer: [
      {
        label: "Razão Social",
        name: "BranchName",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "CNPJ",
        name: "BranchTaxId",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Nome",
        name: "CompanyName",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Telefone",
        name: "BuyerPhone",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "E-mail",
        name: "BuyerEmail",
        width: 1,
        editable: false,
        type: "text",
      },
    ],
    Supplier: [
      {
        label: "Empresa",
        name: "SupplierCompanyName",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "CNPJ",
        name: "SupplierTaxId",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Nome",
        name: "SupplierSalesperson",
        width: 1,
        editable: false,
        type: "text",
      },
    ],
    deliveryAddress: [
      {
        label: "Rua",
        name: "DeliveryAddressStreet",
        width: 2,
        editable: false,
        type: "text",
      },
      {
        label: "Nº",
        name: "DeliveryAddressNum",
        width: 0.75,
        editable: false,
        type: "text",
      },
      {
        label: "Bairro",
        name: "DeliveryAddressBlock",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "CEP",
        name: "DeliveryAddressZip",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Município",
        name: "DeliveryAddressCity",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Estado",
        name: "DeliveryAddressState",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Complemento",
        name: "DeliveryAddressBuilding",
        width: 2,
        editable: false,
        type: "text",
        multiLine: { multiline: true, maxLength: 255 },
      },
      {
        label: "Tipo de Envio",
        name: "ShippingTypeId",
        width: 1,
        editable: true,
        type: "select",
        options: shippingTypesList,
      },
    ],
    billingAddress: [
      {
        label: "Rua",
        name: "BillingAddressStreet",
        width: 2,
        editable: false,
        type: "text",
      },
      {
        label: "Nº",
        name: "BillingAddressNum",
        width: 0.75,
        editable: false,
        type: "text",
      },
      {
        label: "Bairro",
        name: "BillingAddressBlock",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "CEP",
        name: "BillingAddressZip",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Município",
        name: "BillingAddressCity",
        width: 1,
        editable: false,
        type: "text",
      },
      {
        label: "Estado",
        name: "BillingAddressState",
        width: 1,
        editable: false,
        type: "text",
      }
    ],
    payment: [
      {
        label: "Forma de Pagamento",
        name: "PaymentFormId",
        width: 2,
        editable: true,
        type: "select",
        options: paymentFormsList,
      },
      {
        label: "Condição de Pagamento",
        name: "PaymentConditionId",
        width: 2,
        editable: true,
        type: "select",
        options: paymentConditionsList,
      },
      ...(
        quotation && paymentForms.some(
          (pf) => pf.Id === quotation.PaymentFormId && pf.HasComments === true
        )
          ? [{
            label: "Dados para Pagamento",
            name: "PaymentFormComments",
            width: 2,
            editable: true,
            type: "text"
          }]
          : []
      ),
    ],
    items: [
      {
        label: "Código do item",
        name: "ExtItemCode",
        width: 2,
        editable: false,
        type: "text",
      },
      {
        label: "Descrição do item",
        name: "ItemName",
        width: 30,
        editable: false,
        type: "text",
        longField: true,
      },
      {
        label: "Descrição adicional",
        name: "ItemForeignName",
        width: 10,
        editable: false,
        type: "text",
        longField: true,
      },

      {
        label: "Texto livre",
        name: "LineRemarks",
        width: 6,
        editable: true,
        type: "text",
        longField: true,
        inputProps: {
          style: {
            overflow: "auto",   // ativa a rolagem
          }
        },
      },
      {
        label: "Utilização",
        name: "Usage",
        width: 3,
        editable: false,
        type: "text",
      },
      {
        label: "NCM",
        name: "NcmCode",
        width: 2,
        editable: true,
        type: "text",
        longField: false,
      },
      {
        label: "Data Necessária",
        name: "NecessaryDate",
        width: 1,
        editable: false,
        type: "date",
      },
      {
        label: "Previsão de Entrega",
        name: "ExpectedDeliveryDate",
        width: 1,
        editable: true,
        type: "date",
      },
      {
        label: "Quantidade Solicitada",
        name: "QuotedQty",
        width: 2,
        editable: false,
        type: "number",
      },
      {
        label: "Quantidade",
        name: "Quantity",
        width: 2,
        editable: true,
        type: "number",
      },
      {
        label: "Unidade",
        name: "UnitOfMeasurement",
        width: 5,
        editable: false,
        type: "select",
      },
      {
        label: "Valor unitário",
        name: "UnitPrice",
        width: 3,
        editable: true,
        type: "currency",
      },
      {
        label: "% desconto",
        name: "DiscountPercent",
        width: 3,
        editable: true,
        type: "number",
      },
      {
        label: "Valor Unitário de Desconto",
        name: "UnitDiscountValue",
        width: 3,
        editable: true,
        type: "currency",
      },
      {
        label: "Valor unitário pós desconto",
        name: "UnitPriceAfterDiscount",
        width: 3,
        editable: false,
        type: "currency",
      },
      // {
      //   label: "Unidade de Medida",
      //   name: "UnitOfMeasurement",
      //   width: 2,
      //   editable: !!quotation.UomGroup && quotation.UomGroup.trim().length > 0,
      //   type: "text",
      //   options: quotation.UomGroup 
      //     ? quotation.UomGroup.split(";").map(uom => ({
      //         Id: uom,
      //         Description: uom
      //       })) 
      //     : [],
      // },
      {
        label: "Valor Total de Desconto",
        name: "DiscountValue",
        width: 3,
        editable: false,
        type: "currency",
      },
      {
        label: "Total dos itens",
        name: "LineTotal",
        width: 3,
        editable: false,
        type: "currency",
      },
      {
        label: "Total Impostos Adicionais",
        name: "LineTaxOutPriceTotal",
        width: 3,
        editable: false,
        type: "currency",
      },
      {
        label: "Total da linha",
        name: "LineWithTaxTotal",
        width: 3,
        editable: false,
        type: "currency",
      },
      {
        label: "",
        name: "Impostos",
        width: 3,
        editable: false,
        type: "button",
      },
    ],
    additionalExpenses: [
      {
        label: "Tipo de despesa",
        name: "Description",
        editable: false,
        type: "text",
      },
      {
        label: "Valor",
        name: "Value",
        width: 1,
        editable: true,
        type: "currency",
      },
    ],
    attachments: [
      {
        label: "Nome do Arquivo",
        name: "OriginalName",
        width: 4,
        editable: false,
        type: "text",
      },
      {
        label: "Descrição",
        name: "Description",
        width: 6,
        editable: false,
        type: "text",
      },
      {
        label: "Tamanho",
        name: "Size",
        width: 2,
        editable: false,
        type: "text",
      },
      {
        label: "",
        name: "Baixar",
        width: 2,
        editable: false,
        type: "button",
        disabled: !attachments || attachments.length === 0, // Desabilita quando não há anexos
      },
    ],
    observations: [
      { label: "Observações", name: "Comments", width: 100, editable: false, type: "text", multiLine: { multiline: true, maxLength: 255 } },
      { label: "Observações do Fornecedor", name: "SupplierComments", width: 100, editable: true, type: "text", multiLine: { multiline: true, maxLength: 255 } },
      { label: "Observações de Abertura", name: "Header", width: 100, editable: true, type: "text", multiLine: { multiline: true } },
      { label: "Observações de Encerramento", name: "Footer", width: 100, editable: true, type: "text", multiLine: { multiline: true } },
    ],
    footer: [
      { label: "Total dos produtos/serviços", name: "TotalBefDiscount", width: 2, editable: false, type: "currency" },
      ...(
        Array.isArray(tennantConfigs) && tennantConfigs.some(
          (config) => config.Name === "ShowFinancialDiscount" && config.Value === true
        )
          ? [{ label: "Desconto (%)", name: "FinanceDiscount", width: 2, editable: true, type: "number" }]
          : []
      ),
      { label: "Despesas Adicionais", name: "OtherCostsSum", width: 2, editable: false, type: "currency" },
      { label: "Impostos Adicionais", name: "TaxSum", width: 2, editable: false, type: "currency" },
      { label: "Total IPI", name: "IPITotal", width: 2, editable: false, type: "currency" },
      { label: "Valor Total", name: "DocTotal", width: 2, editable: false, type: "currency" },
    ],
  };

  if (!quotation) {
    return (
      <LoadingComponent {...loadingMessage} />
    );
  }

  return (
    <>
      {
        showLogo && (
          <MDBox
            component="img"
            src={quotation.Base64Logo}
            //backgroundColor="blue"
            onClick={() => {
              navigate(`/landing-page`, {
                state: {
                  tenantId: quotation?.TennantId,
                  basePath: window.location.pathname,
                },
              })
            }
            }
            sx={{
              maxWidth: 240,
              maxHeight: 81,
              top: 20,
              position: "fixed",
              right: 50,
              cursor: "pointer",
              zIndex: 9999,
            }}
          />
        )
      }
      <MDBox>
        <MDAccordionSimple
          color="info"
          title="Proposta"
          //fields={[formFields.deliveryAddress, formFields.billingAddress]}
          //nestedTitle={["Endereço de Entrega", "Endereço de Cobrança"]}
          fields={[formFields.Quotation, formFields.Buyer, formFields.Supplier]}
          nestedTitle={["", "Dados Comprador", "Seus Dados"]}
          data={quotation}
          handleInputChange={handleHeaderChange}
        />
        <MDBox mt={4}>
          <MDAccordionSimple
            color="info"
            title="Logística"
            //fields={[formFields.deliveryAddress, formFields.billingAddress]}
            //nestedTitle={["Endereço de Entrega", "Endereço de Cobrança"]}
            fields={[formFields.deliveryAddress]}
            nestedTitle={["Endereço de Entrega"]}
            data={quotation}
            handleInputChange={handleHeaderChange}
          />
          <MDAccordionTable
            color="info"
            title="Itens"
            fields={formFields.items}
            items={renderLines()}
            handleInputChange={handleItemChange}
            handleButtonClick={handleButtonClick}
          />
          <MDAccordionSimple
            color="info"
            title="Pagamento"
            fields={formFields.payment}
            data={quotation}
            handleInputChange={handleHeaderChange}
          />
          <MDAccordionSimple
            color="info"
            title="Observações"
            fields={formFields.observations}
            data={quotation}
            handleInputChange={handleObservationsChange}
          />
          <MDAccordionTable
            color="info"
            title="Despesas Adicionais"
            fields={formFields.additionalExpenses}
            items={renderAdditionalExpenses()}
            handleInputChange={handleAdditionalExpenseChange}
          />
          <MDAccordionTable
            color="info"
            title="Anexos"
            fields={formFields.attachments}
            items={attachmentItems()}
            handleButtonClick={handleAttachmentButtonClick}
          />
          <QuotationHeader
            color="info"
            fields={formFields.footer}
            data={renderFooterValues()}
            handleInputChange={handleFooterChange}
          />
        </MDBox>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={4}
          mb={2}
        >
          <MDButton
            variant="contained"
            color="info"
            size="medium"
            onClick={() => navigate("/ofertas")}
            sx={{ mr: 2 }}
          >
            Voltar
          </MDButton>
          <MDButton
            variant="contained"
            color="success"
            size="medium"
            onClick={handleUpdateClick}
            disabled={!editableQuotationStatuses.includes(quotation.Status)}
            sx={{ mr: 2 }}
          >
            Atualizar
          </MDButton>
          <MDButton
            variant="contained"
            color="error"
            size="medium"
            onClick={handleCancelQuotationClick}
            disabled={!editableQuotationStatuses.includes(quotation.Status)}
            sx={{ mr: 2 }}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="contained"
            color="secondary"
            size="medium"
            onClick={handleDenyQuotationClick}
            disabled={upperCase(quotation.Status) !== 'CRIADA'}
          >
            Não sou eu
          </MDButton>
        </MDBox>
      </MDBox>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <MDBox
          noEndBorder
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: '80%', // Ajustado para ocupar 80% da largura da tela
            maxWidth: 1200, // Opcional: define uma largura máxima
            maxHeight: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDButton
            onClick={handleModalClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "text.secondary",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
              width: 40,
              height: 40,
            }}
          >
            <CloseIcon />
          </MDButton>
          <MDTypography
            variant="h6"
            fontWeight="medium"
            color="text.primary"
            gutterBottom
            sx={{ textAlign: "center" }} // Centraliza o texto
          >
            {selectedItemTax.ItemCode} - {selectedItemTax.ItemName}
          </MDTypography>
          <MDBox sx={{ flex: 1, overflowY: "auto", mt: 2 }}>
            <DataTable
              table={{
                columns: taxModalFields.columns,
                rows: selectedTaxItems,
              }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
              sx={{
                "& .MuiTableCell-root": {
                  padding: "16px 8px",
                },
              }}
            />
          </MDBox>
          <MDBox sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <MDButton
              onClick={handleModalSave}
              variant="contained"
              color="success"
              size="medium"
              disabled={Object.keys(errorMessages).length > 0}
            >
              SALVAR
            </MDButton>
          </MDBox>
        </MDBox>
      </Modal>

      <Dialog
        open={openConfirmDialog}
        onClose={handleConfirmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmar preenchimento"}</DialogTitle>
        <MDButton
          onClick={handleConfirmClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'text.secondary',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
            width: 40,
            height: 40,
          }}
        >
          <CloseIcon />
        </MDButton>
        <DialogContent>
          <MDTypography>{
            `Deseja confirmar o preenchimento da oferta e o envio ao cliente? 
            ${['P'].includes(quotation.Status)
              ? ''
              : 'Caso opte por não confirmar, poderá salvar um esboço para realizar o envio mais tarde.'
            }`
          }</MDTypography>
        </DialogContent>
        <DialogActions>
          {['P'].includes(quotation.Status)
            ? <Button onClick={handleConfirmClose} color="primary">
              Fechar
            </Button>
            : <Button onClick={handleSaveDraft} color="primary" disabled={quotation.Status === 'C'}>
              Salvar esboço
            </Button>
          }
          <Button onClick={handleConfirm} color="primary" disabled={quotation.Status === 'C'}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCancelQuotationDialog}
        onClose={handleCancelQuotationClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmar cancelamento"}</DialogTitle>
        <DialogContent>
          <MDTypography>{"Tem certeza que deseja cancelar essa oferta? Essa ação é irreversível."}</MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelQuotationClose} color="primary">
            Fechar
          </Button>
          <Button onClick={handleConfirmCancelQuotation} color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDenyQuotationDialog}
        onClose={handleDenyQuotationClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmar ação"}</DialogTitle>
        <DialogContent>
          <MDTypography>{"Tem certeza que deseja negar autoridade sobre essa oferta? A empresa emissora será notificada e essa oferta será recusada. Essa ação não poderá ser desfeita."}</MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDenyQuotationClose} color="primary">
            Fechar
          </Button>
          <Button onClick={handleConfirmDenyQuotation} color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuotationDetails;
